<template>
<div>
  <div class="bg-primary-lightest py-10">
    <div class="static-container text-center sm:text-left">
      <h2 v-if="pageTitle" class="text-6xl font-bold">
        {{ pageTitle }}<template v-if="category"> / {{ category.name }}</template>
      </h2>
    </div>
  </div>

  <div class="static-container">
    <nav v-if="categoryRoutes.length > 0" class="mt-5 flex px-4 sm:px-0 flex-wrap space-x-2">
      <router-link v-for="item in categoryRoutes"
                   :key="item.id"
                   :to="item.route"
                   class="mt-5 px-2 py-2 font-medium text-sm rounded transition-all duration-100"
                   :class="{
                     'text-gray hover:text-primary-darker hover:bg-primary-lightest hover:shadow': !item.isActive,
                     'bg-primary-lighter text-primary-darkest shadow': item.isActive,
                   }"
      >
        {{ item.name }}
      </router-link>
    </nav>

    <div v-if="hasArticles" class="mt-10 grid gap-5 lg:grid-cols-3">
      <blog-article-card v-for="article in articles"
                         :key="`blog-article-card-${article.id}`"
                         :article="article"
      />
    </div>
    <p v-else-if="articlesAreLoading" class="px-4 sm:px-0 mt-10">Пожалуйста, подождите.</p>
    <p v-else-if="articlesWereLoaded" class="px-4 sm:px-0 mt-10">Статьи не найдены.</p>
  </div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from '@/zenky'
import InjectsSeo from '@/mixins/InjectsSeo'
import ResponsiveImage from '@/components/images/ResponsiveImage'
import BlogArticleCard from '@/components/blog/BlogArticleCard'

export default {
  mixins: [InjectsSeo],

  props: {
    categoryShortId: {
      required: false,
      type: String,
    },
  },

  components: {
    ResponsiveImage,
    BlogArticleCard,
  },

  metaInfo() {
    return this.getMetaInfoFromSettings(this.blogSeo)
  },

  created() {
    this.loadCategories()
  },

  mounted() {
    this.loadArticles()
  },

  computed: {
    ...mapGetters({
      settings: 'settings/blogList',
      blogSeo: 'settings/blogSeo',
      categories: 'articles/categories',
    }),

    category() {
      if (!this.categoryShortId) {
        return null
      }

      return this.categories.find(category => category.short_id === this.categoryShortId)
    },

    pageTitle() {
      return this.settings.title
    },

    hasArticles() {
      return this.articles.length > 0
    },

    hasMorePages() {
      return this.maxPage > this.page
    },

    categoryRoutes() {
      if (!this.categories.length) {
        return [];
      }

      const routes = this.categories.map((category) => ({
        id: `BlogCategory-${category.id}`,
        name: category.name,
        isActive: this.categoryShortId && this.categoryShortId === category.short_id,
        route: {
          name: 'blog-list-category',
          params: {
            categoryShortId: category.short_id,
            categorySlug: category.slug,
          },
        },
      }));

      routes.unshift({
        id: 'BlogListAllCategories',
        name: 'Все записи',
        isActive: !this.categoryShortId,
        route: {
          name: 'blog-list',
        },
      });

      return routes;
    },
  },

  methods: {
    ...mapActions({
      loadCategories: 'articles/loadCategories',
    }),

    loadArticles() {
      if (this.articlesAreLoading) {
        return
      }

      this.articlesAreLoading = true

      const params = {
        page: this.page,
        with: 'media,category',
        order_by: '-created_at'
      }

      if (this.categoryShortId) {
        params.category_id = this.categoryShortId
      }

      axios.get('articles', { params })
        .then(({ data }) => {
          this.articlesAreLoading = false
          this.articlesWereLoaded = true
          this.maxPage = data.meta.pagination.total_pages

          data.data.forEach(article => this.articles.push(article))
        })
        .catch(err => {
          this.articlesAreLoading = false
          this.articlesWereLoaded = true
          console.error(err)
        })
    },

    loadNextPage() {
      this.page++
      this.loadArticles()
    },
  },

  watch: {
    categoryShortId() {
      this.page = 1
      this.articles = []
      this.loadArticles()
    },
  },

  data() {
    return {
      articlesAreLoading: false,
      articlesWereLoaded: false,
      articles: [],
      page: 1,
      maxPage: 1,
    }
  }
}
</script>

<style lang="less">
@import "~@/assets/less/components/blog-articles-header";

.blog-articles-preview-list {
  @apply my-8 px-4;

  .blog-article-preview-item {
    @apply w-full flex flex-wrap mb-16;

    &:last-child {
      @apply mb-0;
    }

    .artcle-preview-image {
      @apply w-full mb-2;

      img {
        @apply w-full rounded;
      }
    }

    .article-preview-content {
      @apply w-full;

      .article-preview-meta {
        @apply text-grey mb-2 text-sm;
      }

      .article-preview-title {
        @apply text-xl font-normal mb-2;

        a {
          @apply text-primary;

          &:hover {
            @apply underline;
          }
        }
      }

      .article-preview-intro {
        @apply text-base font-normal leading-26;
      }
    }
  }
}

@screen md {
  .blog-articles-preview-list {
    .blog-article-preview-item {
      @apply flex-no-wrap;

      .artcle-preview-image {
        @apply w-1/4 mr-4 mb-0;
      }

      .article-preview-content {
        @apply w-3/4;
      }
    }
  }
}
</style>
